import React, { useEffect } from 'react';

import type { SiteChromeDynamicContextProviderProps } from './types';
/* 
  This context must be imported using the client-site-chrome-ui alias  
  i.e. client-site-chrome-ui/context/DynamicContext/context
  to ensure it is always resolved from the same location by yarn PNP
  otherwise an error can be thrown that the context is not defined 
  i.e.'useSiteChromeDynamicContext must be used within a SiteChromeDynamicContextProvider'
*/
import { SiteChromeDynamicContext } from 'client-site-chrome-ui/context/SiteChromeDynamicContext/SiteChromeDynamicContext';
import { useSiteChromeStaticContext } from 'client-site-chrome-ui/context/SiteChromeStaticContext/useSiteChromeStaticContext';
import { useContentOverlay } from 'client-site-chrome-ui/hooks/useContentOverlay';
import { useDepartmentsNavigation } from 'client-site-chrome-ui/hooks/useDepartmentsNavigation';
import { useLocale } from 'client-site-chrome-ui/hooks/useLocale/useLocale';
import { useLocaleModal } from 'client-site-chrome-ui/hooks/useLocaleModal/useLocaleModal';
import { useNavigationModal } from 'client-site-chrome-ui/hooks/useNavigationModal/useNavigationModal';

export const SiteChromeDynamicContextProvider = ({
  children,
  ...props
}: SiteChromeDynamicContextProviderProps) => {
  const { cookies } = useSiteChromeStaticContext();
  const departmentsNavigation = useDepartmentsNavigation();
  const contentOverlay = useContentOverlay();
  const localeModal = useLocaleModal();
  const navigationModal = useNavigationModal();
  const contextValue = {
    navigationModal,
    contentOverlay,
    departmentsNavigation,
    locale: useLocale({ cookies }),
    localeModal,
  };

  useEffect(() => {
    if (localeModal.isOpen) {
      if (navigationModal.isOpen) {
        navigationModal.closeWithoutTracking();
      }
      contentOverlay.open(0);
    } else {
      contentOverlay.close();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeModal.isOpen]);

  return (
    <SiteChromeDynamicContext.Provider value={contextValue} {...props}>
      {children}
    </SiteChromeDynamicContext.Provider>
  );
};
